import { type ChangeEvent, type FC, useCallback, useEffect, useMemo, useState } from 'react';

import { type SelectValue } from '@mui/base';
import { type SelectChangeEventType, type SelectOptionDefinition } from '@mui/base/useSelect/useSelect.types';
import { TripFilterMenu } from '@vakantiesnl/components/src/molecules/TripFilterMenu';
import { TripFilterMenuVariantB } from '@vakantiesnl/components/src/molecules/TripFilterMenu/TripFilterMenuVariantB';
import { useTripFilterContext } from '@vakantiesnl/services/src/context/useTripFilterContext';
import { airports, mealplans } from '@vakantiesnl/services/src/route-params';
import { usePartyComposition } from '@vakantiesnl/services/src/stores';
import { buildSummarizedPartyString } from '@vakantiesnl/services/src/util/partyUtils';
import { type MicroCopy, type Search } from '@vakantiesnl/types';

type Props = {
	microCopies: MicroCopy;
	/** For experimenting */
	hasDurationRange?: boolean;
};

export const TripFilterMenuBlock: FC<Props> = ({ microCopies, hasDurationRange }) => {
	const {
		state,
		computed: { durations },
		dispatch: {
			updatePartyComposition,
			updateAirportsTripFilter,
			updateDepartureDateTripFilter,
			updateDurationRangeTripFilter,
			updateMealplanTripFilter,
			updateInvalidPreselection,
			updateSelectedTrip,
			updateSelectedTripDuration,
			updateSelectedFlight,
			updateSelectedRoomType,
			updateSelectedMealPlan,
			updateWithSelectableTransfer,
		},
	} = useTripFilterContext();

	const party = usePartyComposition();
	const [partyString, setPartyString] = useState(buildSummarizedPartyString(party, microCopies));

	/** Upon a manual filter selection, clear the query parameters used for shareable URL */
	const clearSelected = useCallback(() => {
		updateInvalidPreselection(false);
		updateSelectedFlight(undefined);
		updateSelectedRoomType(undefined);
		updateSelectedMealPlan(undefined);
	}, [updateInvalidPreselection, updateSelectedFlight, updateSelectedMealPlan, updateSelectedRoomType]);

	const onSetDepartureDate = useCallback(
		(departureDate: string[]) => {
			if (departureDate.length) {
				updateDepartureDateTripFilter([departureDate[0]]);
				/** Remove pre-selection of and price bar date and duration for matrix */
				updateSelectedTrip(undefined);
				updateSelectedTripDuration(undefined);
				clearSelected();
			}
		},
		[clearSelected, updateDepartureDateTripFilter, updateSelectedTrip, updateSelectedTripDuration],
	);

	const onChangeAirport = useCallback(
		(_: SelectChangeEventType, options: SelectValue<string, true>) => {
			const selectedAirports = [...options];
			updateAirportsTripFilter(selectedAirports || []);
			clearSelected();
		},
		[clearSelected, updateAirportsTripFilter],
	);

	const onChangeDurationRange = useCallback(
		(_: SelectChangeEventType, options: SelectValue<string, true>) => {
			const selectedDurationRange = options.flatMap((s) => s.split(',').map(Number)).sort((a, b) => a - b);

			updateDurationRangeTripFilter(selectedDurationRange);

			/** Reset selected trip duration when the duration range is not including the selected duration  */
			if (state.selected.duration && !selectedDurationRange.includes(Number(state.selected.duration))) {
				updateSelectedTripDuration(undefined);
			}
			clearSelected();
		},
		[clearSelected, state.selected.duration, updateDurationRangeTripFilter, updateSelectedTripDuration],
	);

	const onChangeWithSelectableTransfer = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			updateWithSelectableTransfer(e.target.checked);
		},
		[updateWithSelectableTransfer],
	);

	const onPartyUpdate = useCallback(
		(party: Search.PartyComposition[]) => {
			setPartyString(buildSummarizedPartyString(party, microCopies));
			clearSelected();
		},
		[clearSelected, microCopies],
	);

	const onClosePartyPicker = useCallback(
		(party: Search.PartyComposition[]) => {
			updatePartyComposition(party);
			clearSelected();
		},
		[clearSelected, updatePartyComposition],
	);

	const onChangeMealplan = useCallback(
		(_: SelectChangeEventType, options: SelectValue<string, true>) => {
			const selectedMealplans = [...options];
			updateMealplanTripFilter(selectedMealplans || []);
			clearSelected();
		},
		[updateMealplanTripFilter, clearSelected],
	);

	// Update the party composition in the partypicker when the party composition is being read from local storage
	useEffect(() => {
		if (party) {
			setPartyString(buildSummarizedPartyString(party, microCopies));
		}
	}, [party, microCopies]);

	const airportOptions = useMemo((): SelectOptionDefinition<string>[] => {
		return Object.entries(airports).map(([airportCode, airportLabel]) => ({
			label: airportLabel,
			value: airportCode,
		}));
	}, []);

	const mealplanOptions = useMemo((): SelectOptionDefinition<string>[] => {
		return Object.entries(mealplans).map(([mealplanCode, mealplanLabel]) => ({
			label: mealplanLabel,
			value: mealplanCode,
		}));
	}, []);

	if (hasDurationRange) {
		return (
			<TripFilterMenuVariantB
				departureDate={state.departureDate}
				airports={state.airports}
				party={party}
				durations={durations}
				mealplans={state.mealplans}
				partyString={partyString}
				onPartyUpdate={onPartyUpdate}
				onChangeAirport={onChangeAirport}
				onChangeDurationRange={onChangeDurationRange}
				onClosePartyPicker={onClosePartyPicker}
				microCopies={microCopies}
				airportOptions={airportOptions}
				mealplanOptions={mealplanOptions}
				onChangeMealplan={onChangeMealplan}
				onSetDepartureDate={onSetDepartureDate}
				withSelectableTransfer={state.withSelectableTransfer}
				onWithSelectableTransfer={onChangeWithSelectableTransfer}
			/>
		);
	}
	return (
		<TripFilterMenu
			departureDate={state.departureDate}
			airports={state.airports}
			party={party}
			durations={durations}
			mealplans={state.mealplans}
			partyString={partyString}
			onPartyUpdate={onPartyUpdate}
			onChangeAirport={onChangeAirport}
			onChangeDurationRange={onChangeDurationRange}
			onClosePartyPicker={onClosePartyPicker}
			microCopies={microCopies}
			airportOptions={airportOptions}
			mealplanOptions={mealplanOptions}
			onChangeMealplan={onChangeMealplan}
			onSetDepartureDate={onSetDepartureDate}
			withSelectableTransfer={state.withSelectableTransfer}
			onWithSelectableTransfer={onChangeWithSelectableTransfer}
		/>
	);
};
